import {
  Button,
  CircularProgress,
  DialogActions,
  Divider,
  List,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useContext, useEffect, useState } from "react";

import { Comment } from "../../generated/gql/graphql";
import useAssetCommentMutation from "../../hooks/mutation/comments/use-asset-comment-mutation";
import useAssetComments from "../../hooks/query/comments/use-asset-comments";
import useText from "../../hooks/use-text";
import { UserContext } from "../../providers/user-provider";
import { parseType } from "../../utils/graphql";
import { LocalizedStrings, SupportedLanguages } from "../../utils/lang";

import { EditComment } from "./parts/edit-comment";
import { ListCommentItem } from "./parts/list-comment-item";
import { ReplyComment } from "./parts/reply-comment";
import { SubComments } from "./parts/sub-comments";

interface CommentsProps {
  isOpen: boolean;
  assetId: string;
  closeDialog: () => void;
}

export default function Comments(props: CommentsProps): React.ReactElement {
  const { isOpen, assetId, closeDialog } = props;

  const { strings } = useText(local);

  const [editCommentId, setEditCommentId] = useState<string>();
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState<string>();

  const { me } = useContext(UserContext);
  const { createComment, deleteComment, updateComment } =
    useAssetCommentMutation();

  const { data, loading, error } = useAssetComments({
    filter: {
      assetId: {
        eq: assetId,
      },
    },
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    setComments(parseType<Comment[]>("comments", data));
  }, [data]);

  async function internalAddComment(
    comment: string,
    parentCommentId?: string
  ): Promise<void> {
    if (!comment) {
      return;
    }

    try {
      const createdComment = await createComment({
        assetId: assetId,
        comment: comment,
        parentId: parentCommentId,
      });

      setComments((prev) => prev.concat([createdComment]));
      setCommentText("");
    } catch (error) {
      enqueueSnackbar(strings.errors.failedCreation, { variant: "error" });
      return;
    }
  }

  async function internalDeleteComment(id: string): Promise<void> {
    try {
      await deleteComment(id);
      setComments((prev) => prev.filter((comment) => comment.id !== id));
    } catch (error) {
      enqueueSnackbar(strings.errors.failedDeletion, { variant: "error" });
    }
  }

  async function internalEditComment(id: string, value: string): Promise<void> {
    try {
      await updateComment(id, {
        comment: value,
      });

      const updatedComments = comments.map((v) => {
        if (v.id !== id) {
          return v;
        }

        return {
          ...v,
          comment: value,
        };
      });

      setComments(updatedComments);
      setEditCommentId(undefined);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(strings.errors.failedUpdating, { variant: "error" });
    }
  }

  return (
    <Dialog onClose={closeDialog} open={isOpen}>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {strings.title.replace("{{count}}", comments?.length.toString() || "")}
      </DialogTitle>
      <DialogContent dividers className="max-h-[550px]">
        <List className="pt-0 min-w-[550px] p-0 mb-[-1px]">
          {loading && (
            <Box className="text-center">
              <CircularProgress />
            </Box>
          )}
          {error && <div>{strings.errors.general}</div>}
          {comments && comments.length === 0 && (
            <Box className="text-center">
              <p>{strings.empty}</p>
            </Box>
          )}
          {comments &&
            comments.map(
              (comment) =>
                !comment.parentId && (
                  <div key={comment.id}>
                    {editCommentId === comment.id ? (
                      <EditComment
                        comment={comment}
                        editCommentId={editCommentId}
                        handleEditComment={internalEditComment}
                      />
                    ) : (
                      <ListCommentItem
                        comment={comment}
                        setEditCommentId={setEditCommentId}
                        editable={me?.id === comment.userId}
                        handleRemoveComment={internalDeleteComment}
                      />
                    )}
                    <SubComments
                      removeComment={internalDeleteComment}
                      commentId={comment.id}
                      comments={comments.filter(
                        (v) => v.parentId === comment.id
                      )}
                      editCommentId={editCommentId}
                      handleEditComment={(id, comment) =>
                        internalAddComment(comment, id)
                      }
                      setEditCommentId={setEditCommentId}
                    />
                    <ReplyComment comment={comment} />
                    <Divider className="mx-5" />
                  </div>
                )
            )}
        </List>
      </DialogContent>
      <DialogActions className="items-end space-x-2">
        <TextField
          onChange={(e) => setCommentText(e.target.value)}
          value={commentText}
          label={strings.comment.label}
          placeholder={strings.comment.placeholder}
          className="w-full"
          multiline
        />
        <Button onClick={closeDialog}>{strings.cancelCta}</Button>
        <Button
          disabled={!commentText}
          onClick={() => internalAddComment(commentText || "")}
        >
          {strings.addCta}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type LocalSchema = {
  title: string;
  comment: {
    label: string;
    placeholder: string;
  };
  empty: string;
  errors: {
    general: string;
    failedCreation: string;
    failedUpdating: string;
    failedDeletion: string;
  };
  addCta: string;
  cancelCta: string;
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    title: "Asset Comments ({{count}})",
    comment: {
      label: "Your Comment",
      placeholder: "Type something here…",
    },
    empty: "No comments yet.",
    errors: {
      general: "Something went wrong. Please retry it again.",
      failedCreation: "Failed to create comment",
      failedUpdating: "Failed to update comment",
      failedDeletion: "Failed to delete comment",
    },
    addCta: "Add",
    cancelCta: "Cancel",
  },
};
